import { GetCachedLicenseOptions, CachedLicense } from "../../../types"
import * as Redis from "./redis"
import * as Licenses from "../licenses"
import { tenancy, env, context } from "@budibase/backend-core"
import tracer from "dd-trace"

const EXPIRY_SECONDS = 3600

export const refresh = async () => {
  await invalidate()
  await getCachedLicense()
}

let _getCachedLicense = async (
  ctx?: any,
  opts?: GetCachedLicenseOptions
): Promise<CachedLicense> => {
  const {
    populateLicense = Licenses.getLicense,
    populateFreeLicense = Licenses.getFreeLicense,
  } = opts || {}

  return await tracer.trace("getCachedLicense", async span => {
    const fromContext = context.getLicense()
    if (fromContext) {
      span.addTags({
        foundInContext: true,
        features: fromContext.features,
        plan: fromContext.plan,
        quotas: fromContext.quotas,
      })
      return fromContext
    }
    const tenantId = tenancy.getTenantId()
    const client = await Redis.getClient()
    let license: CachedLicense | undefined = await client.get(tenantId)
    span.addTags({ tenantId, foundInCache: !!license })

    if (license) {
      span.addTags({
        foundInCache: true,
        refreshedAt: license.refreshedAt,
        features: license.features,
        plan: license.plan,
        quotas: license.quotas,
      })
      return license
    }

    license = await tracer.trace(
      "populateLicense",
      async () => await populateLicense(tenantId)
    )
    span.addTags({ populatedLicense: !!license })

    if (!license) {
      license = tracer.trace("populateFreeLicense", () =>
        populateFreeLicense(ctx, tenantId)
      )
      span.addTags({ populatedFreeLicense: !!license })
    }

    license.refreshedAt = new Date().toISOString()
    span.addTags({
      refreshedAt: license.refreshedAt,
      features: license.features,
      plan: license.plan,
      quotas: license.quotas,
      expirySeconds: EXPIRY_SECONDS,
    })
    await client.store(tenantId, license, EXPIRY_SECONDS)

    return license
  })
}

// special case for re-using pro mocks
// in other services - can't mock this externally
// whenever pro is bundled into a single file
if (env.isJest()) {
  _getCachedLicense = jest.fn()
}

export const getCachedLicense = _getCachedLicense

export const invalidate = async () => {
  const tenantId = tenancy.getTenantId()
  const client = await Redis.getClient()
  await client.delete(tenantId)
}
